import React from 'react';
import './EditCandidateModal.css';
import RoleField from './RoleField';

// Single source of truth for the candidate add/edit form, shared by RecruiterPortal.js and
// CandidateArchive.js. Previously each page had its own independently-copied form that had
// drifted apart over time -- most seriously, Candidate Archive's Screening Status dropdown
// didn't even have a "Pass" option (only Screened/FADV Sent/Ready for Interview/Hired/Rejected,
// values that don't match what's actually used anywhere else in the app), and its BG/Drug/
// Physical/GCIC/FADV dropdowns each had their own different, partly-incompatible option sets.
// This uses Recruiter Portal's field set and option values throughout, since those are the ones
// that actually match real data/usage elsewhere in the codebase (see CLAUDE.md's documented
// valid values for BG/DOT/MEC/FADV status).
//
// Purely presentational -- the caller owns formData/setFormData state and the actual submit
// logic (each page's PUT/POST call, success handling, etc. stays exactly as it was). This
// component only renders the fields and wires onChange back into the caller's state.
export default function EditCandidateModal({
  mode, // 'add' | 'edit'
  formData,
  setFormData,
  companies,
  csas,
  onSubmit,
  onCancel,
  canRemoveBlacklist = false, // admins + FA override list (see shared/overrideUsers.js)
  extraFields = null, // e.g. the Recruiter dropdown (shared/RecruiterField.js), admins + GV on edit only
}) {
  const isEdit = mode === 'edit';
  // A blacklist already saved on this candidate can only be removed by canRemoveBlacklist users
  const blacklistLocked = isEdit && formData._wasBlacklisted && !canRemoveBlacklist;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content ecm" onClick={(e) => e.stopPropagation()}>
        <h3>{isEdit ? 'Edit Candidate' : 'Add New Candidate'}</h3>
        <form onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Company *</label>
              <select
                value={formData.company_id || ''}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value, csa_id: '' })}
                required
              >
                <option value="">Select Company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>CSA *</label>
              <select
                value={formData.csa_id || ''}
                onChange={(e) => {
                  const csa = csas.find(c => String(c.id) === e.target.value);
                  setFormData({ ...formData, csa_id: e.target.value, company_id: csa ? String(csa.company_id) : formData.company_id });
                }}
                required
              >
                <option value="">Select CSA</option>
                {csas.filter(c => !formData.company_id || String(c.company_id) === String(formData.company_id)).map(csa => (
                  <option key={csa.id} value={csa.id}>{csa.name}</option>
                ))}
              </select>
            </div>

            <RoleField value={formData.job_role || ''} onChange={v => setFormData(f => ({ ...f, job_role: v }))} />

            <div className="form-group">
              <label>Candidate Name *</label>
              <input type="text" value={formData.candidate_name} onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })} required />
            </div>

            {(
              <div className="form-group">
                <label>FedEx History</label>
                <select
                  value={formData.fedex_history || ''}
                  onChange={(e) => setFormData({ ...formData, fedex_history: e.target.value, fadv_file_transfer: e.target.value ? formData.fadv_file_transfer : '' })}
                >
                  <option value="">None</option>
                  <option value="Ex-FedEx">Ex-FedEx</option>
                  <option value="Current FedEx">Current FedEx</option>
                </select>
              </div>
            )}

            {formData.fedex_history && (
              <div className="form-group">
                <label>FADV File</label>
                <select value={formData.fadv_file_transfer || ''} onChange={(e) => setFormData({ ...formData, fadv_file_transfer: e.target.value })}>
                  <option value=""></option>
                  <option value="File Transferred">File Transferred</option>
                  <option value="Not Transferred">Not Transferred</option>
                </select>
                {formData.fadv_file_transfer === 'Not Transferred' && (
                  <small style={{ color: 'var(--muted, #8892b0)' }}>No FA screenshot will be asked for on Send to Client.</small>
                )}
              </div>
            )}

            <div className="form-group">
              <label>FedEx ID</label>
              <input type="text" placeholder="e.g., 123456" value={formData.fedex_id} onChange={(e) => setFormData({ ...formData, fedex_id: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Location</label>
              <input type="text" placeholder="e.g., Atlanta, GA" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Screening Date</label>
              <input type="date" value={formData.screening_date} onChange={(e) => setFormData({ ...formData, screening_date: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Experience</label>
              <input type="text" placeholder="e.g., Delivery Driver and Package Loader" value={formData.last_worked} onChange={(e) => setFormData({ ...formData, last_worked: e.target.value })} />
            </div>

            {isEdit && formData.experience_summary && (
              <div className="form-group full-width">
                <label>Experience Summary (AI-generated, read-only)</label>
                <textarea value={formData.experience_summary} disabled rows="2" style={{ background: 'var(--surface2, #f6f8fc)', color: 'var(--text2, #4a5278)', cursor: 'not-allowed' }} />
              </div>
            )}

            <div className="form-group">
              <label>Mountain Rating</label>
              <select value={formData.mountain_rating || ''} onChange={(e) => setFormData({ ...formData, mountain_rating: e.target.value })}>
                <option value="">No Rating</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>BG Status</label>
              <select value={formData.bg_status} onChange={(e) => setFormData({ ...formData, bg_status: e.target.value })}>
                <option value="">-</option>
                <option value="Pending">Pending</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>
            </div>

            <div className="form-group">
              <label>Drug Status</label>
              <select value={formData.drug_status} onChange={(e) => setFormData({ ...formData, drug_status: e.target.value, drug_test_pass_date: e.target.value !== 'Pass' ? '' : formData.drug_test_pass_date })}>
                <option value="">-</option>
                <option value="Pending">Pending</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>
            </div>

            {formData.drug_status === 'Pass' && (
              <div className="form-group">
                <label>Drug Test Pass Date <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="date"
                  value={formData.drug_test_pass_date || ''}
                  onChange={(e) => setFormData({ ...formData, drug_test_pass_date: e.target.value })}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Physical Status</label>
              <select value={formData.physical_status} onChange={(e) => setFormData({ ...formData, physical_status: e.target.value })}>
                <option value="">-</option>
                <option value="Pending">Pending</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="MEC Uploaded">MEC Uploaded</option>
              </select>
            </div>

            <div className="form-group">
              <label>GCIC Status</label>
              <select value={formData.gcic_status} onChange={(e) => setFormData({ ...formData, gcic_status: e.target.value })}>
                <option value="">-</option>
                <option value="Pending">Pending</option>
                <option value="Uploaded">Uploaded</option>
                <option value="Not Required">Not Required</option>
                <option value="Pass">Pass</option>
              </select>
            </div>

            <div className="form-group">
              <label>Screening Status</label>
              <select value={formData.screening_status} onChange={(e) => setFormData({ ...formData, screening_status: e.target.value })}>
                <option value="">-</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="Not Responding">Not Responding</option>
                <option value="Not Interested">Not Interested</option>
              </select>
            </div>

            <div className="form-group">
              <label>FADV Status</label>
              <select value={formData.fadv_status} onChange={(e) => setFormData({ ...formData, fadv_status: e.target.value })}>
                <option value="">-</option>
                <option value="Sent">Sent</option>
                <option value="Completed">Completed</option>
                <option value="Eligible">Eligible</option>
                <option value="Ineligible">Ineligible</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Indeed URL</label>
              <input type="url" value={formData.indeed_url} onChange={(e) => setFormData({ ...formData, indeed_url: e.target.value })} />
            </div>

            <div className="form-group full-width">
              <label>Remarks</label>
              <textarea value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} rows="3" />
            </div>

            <div className="form-group full-width">
              <label>
                <input
                  type="checkbox"
                  checked={!!formData.is_high_priority}
                  onChange={(e) => setFormData({ ...formData, is_high_priority: e.target.checked ? 1 : 0 })}
                />
                High Priority
              </label>
            </div>

            {(
              <div className="form-group full-width">
                <label title={blacklistLocked ? 'Only admins, GV, Atiya or Maddy can remove a blacklist' : undefined}>
                  <input
                    type="checkbox"
                    checked={!!formData.blacklisted}
                    disabled={blacklistLocked}
                    onChange={(e) => setFormData({ ...formData, blacklisted: e.target.checked, blacklist_reason: e.target.checked ? formData.blacklist_reason : '' })}
                  />
                  Blacklist this candidate
                  {blacklistLocked && <small style={{ color: 'var(--muted, #8892b0)', marginLeft: 6 }}>(only admins, GV, Atiya or Maddy can remove)</small>}
                </label>
                {formData.blacklisted && (
                  <textarea
                    value={formData.blacklist_reason || ''}
                    onChange={(e) => setFormData({ ...formData, blacklist_reason: e.target.value })}
                    placeholder="Reason for blacklisting (required)"
                    rows="2"
                    required
                    disabled={blacklistLocked}
                    style={{ marginTop: 6 }}
                  />
                )}
              </div>
            )}
            {extraFields}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
            <button type="submit" className="submit-btn">{isEdit ? 'Update' : 'Create'} Candidate</button>
          </div>
        </form>
      </div>
    </div>
  );
}
